import { useState } from 'react'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type {
  ConfirmedHarmonisedStandard,
  HarmonisedStandardRecommendation,
} from '@/v2/lib/confirmedHarmonisedStandards'

type StandardRow = {
  designation: string
  title: string
}

function toRows(standards: ConfirmedHarmonisedStandard[]): StandardRow[] {
  if (standards.length === 0) {
    return [{ designation: '', title: '' }]
  }
  return standards.map((standard) => ({
    designation: standard.designation,
    title: standard.title ?? '',
  }))
}

function rowsToPayload(rows: StandardRow[]): ConfirmedHarmonisedStandard[] {
  const payload: ConfirmedHarmonisedStandard[] = []
  for (const row of rows) {
    const designation = row.designation.trim()
    if (designation.length === 0) {
      continue
    }
    const title = row.title.trim()
    payload.push({
      designation,
      ...(title.length > 0 ? { title } : {}),
    })
  }
  return payload
}

function recommendationKey(recommendation: HarmonisedStandardRecommendation): string {
  return `${recommendation.designation}::${recommendation.title ?? ''}`
}

function mergeRowsWithSelectedRecommendations(
  rows: StandardRow[],
  recommendations: HarmonisedStandardRecommendation[],
  selectedKeys: string[]
): StandardRow[] {
  if (selectedKeys.length === 0) {
    return rows
  }

  const selected = recommendations.filter((recommendation) =>
    selectedKeys.includes(recommendationKey(recommendation))
  )
  const existingDesignations = new Set(
    rowsToPayload(rows).map((standard) => standard.designation.toLowerCase())
  )
  const nextRows = [...rows]

  for (const recommendation of selected) {
    const designation = recommendation.designation.trim()
    if (
      designation.length === 0 ||
      existingDesignations.has(designation.toLowerCase())
    ) {
      continue
    }
    existingDesignations.add(designation.toLowerCase())
    nextRows.push({
      designation,
      title: recommendation.title?.trim() ?? '',
    })
  }

  return nextRows
}

type ConfirmedHarmonisedStandardsEditSheetProps = {
  standards: ConfirmedHarmonisedStandard[]
  open: boolean
  onOpenChange: (open: boolean) => void
  i18nKeyPrefix: string
  onSave: (standards: ConfirmedHarmonisedStandard[]) => Promise<void>
  onFetchRecommendations: () => Promise<HarmonisedStandardRecommendation[]>
  isSaving?: boolean
  isLoadingRecommendations?: boolean
}

export function ConfirmedHarmonisedStandardsEditSheet({
  standards,
  open,
  onOpenChange,
  i18nKeyPrefix,
  onSave,
  onFetchRecommendations,
  isSaving = false,
  isLoadingRecommendations = false,
}: ConfirmedHarmonisedStandardsEditSheetProps) {
  const { t } = useTranslation('common')
  const editDialogKey = `${i18nKeyPrefix}.editDialog`

  const [rows, setRows] = useState<StandardRow[]>(() => toRows(standards))
  const [recommendations, setRecommendations] = useState<
    HarmonisedStandardRecommendation[]
  >([])
  const [selectedRecommendationKeys, setSelectedRecommendationKeys] = useState<
    string[]
  >([])

  function updateRow(
    index: number,
    field: keyof StandardRow,
    value: string
  ): void {
    setRows((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    )
  }

  function addRow(): void {
    setRows((current) => [...current, { designation: '', title: '' }])
  }

  function removeRow(index: number): void {
    setRows((current) => {
      if (current.length <= 1) {
        return [{ designation: '', title: '' }]
      }
      return current.filter((_, rowIndex) => rowIndex !== index)
    })
  }

  async function handleGetRecommendations(): Promise<void> {
    try {
      const result = await onFetchRecommendations()
      setRecommendations(result)
      setSelectedRecommendationKeys([])
    } catch {
      toast.error(t(`${editDialogKey}.recommendationsFailed`))
    }
  }

  function toggleRecommendation(key: string, checked: boolean): void {
    setSelectedRecommendationKeys((current) => {
      if (checked) {
        if (current.includes(key)) {
          return current
        }
        return [...current, key]
      }
      return current.filter((entry) => entry !== key)
    })
  }

  function addSelectedRecommendations(): void {
    if (selectedRecommendationKeys.length === 0) {
      return
    }

    setRows((current) =>
      mergeRowsWithSelectedRecommendations(
        current,
        recommendations,
        selectedRecommendationKeys
      )
    )
    setSelectedRecommendationKeys([])
  }

  async function handleSave(): Promise<void> {
    const payload = rowsToPayload(
      mergeRowsWithSelectedRecommendations(
        rows,
        recommendations,
        selectedRecommendationKeys
      )
    )
    if (payload.length === 0) {
      toast.error(t(`${editDialogKey}.designationRequired`))
      return
    }
    try {
      await onSave(payload)
      onOpenChange(false)
      toast.success(t(`${editDialogKey}.saveSuccess`))
    } catch {
      toast.error(t(`${editDialogKey}.saveFailed`))
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side='right'
        className='flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl'
      >
        <div className='flex shrink-0 flex-col gap-4 px-4 pt-4'>
          <SheetHeader className='p-0 text-start pe-10'>
            <SheetTitle className='text-lg leading-none'>
              {t(`${editDialogKey}.title`)}
            </SheetTitle>
            <SheetDescription>{t(`${editDialogKey}.description`)}</SheetDescription>
          </SheetHeader>
        </div>

        <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4'>
          <div className='space-y-3'>
            {rows.map((row, index) => (
              <div
                key={`standard-row-${index}`}
                className='grid gap-3 rounded-md border p-3 sm:grid-cols-[1fr_1fr_auto]'
              >
                <div className='space-y-1.5'>
                  <Label htmlFor={`standard-designation-${index}`}>
                    {t(`${editDialogKey}.designationLabel`)}
                  </Label>
                  <Input
                    id={`standard-designation-${index}`}
                    value={row.designation}
                    onChange={(event) => {
                      updateRow(index, 'designation', event.target.value)
                    }}
                    placeholder={t(`${editDialogKey}.designationPlaceholder`)}
                  />
                </div>
                <div className='space-y-1.5'>
                  <Label htmlFor={`standard-title-${index}`}>
                    {t(`${editDialogKey}.titleLabel`)}
                  </Label>
                  <Input
                    id={`standard-title-${index}`}
                    value={row.title}
                    onChange={(event) => {
                      updateRow(index, 'title', event.target.value)
                    }}
                    placeholder={t(`${editDialogKey}.titlePlaceholder`)}
                  />
                </div>
                <div className='flex items-end'>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    onClick={() => {
                      removeRow(index)
                    }}
                    aria-label={t(`${editDialogKey}.removeRow`)}
                  >
                    <Trash2 className='size-4' />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <Button type='button' variant='outline' size='sm' onClick={addRow}>
            <Plus className='mr-2 size-4' />
            {t(`${editDialogKey}.addRow`)}
          </Button>

          <div className='space-y-3 rounded-md border p-3'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <p className='text-sm font-medium'>
                {t(`${editDialogKey}.recommendationsTitle`)}
              </p>
              <Button
                type='button'
                variant='outline'
                size='sm'
                disabled={isLoadingRecommendations}
                onClick={() => {
                  void handleGetRecommendations()
                }}
              >
                {isLoadingRecommendations ? (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                ) : null}
                {t(`${editDialogKey}.getRecommendations`)}
              </Button>
            </div>
            {recommendations.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                {t(`${editDialogKey}.recommendationsEmpty`)}
              </p>
            ) : (
              <div className='space-y-2'>
                <p className='text-sm text-muted-foreground'>
                  {t(`${editDialogKey}.recommendationsHint`)}
                </p>
                {recommendations.map((recommendation) => {
                  const key = recommendationKey(recommendation)
                  const checked = selectedRecommendationKeys.includes(key)
                  return (
                    <label
                      key={key}
                      className='flex cursor-pointer items-start gap-3 rounded-md border p-3'
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) => {
                          toggleRecommendation(key, value === true)
                        }}
                      />
                      <span className='space-y-1 text-sm'>
                        <span className='block font-medium'>
                          {recommendation.designation}
                        </span>
                        {recommendation.title ? (
                          <span className='block text-muted-foreground'>
                            {recommendation.title}
                          </span>
                        ) : null}
                        {recommendation.reason ? (
                          <span className='block text-xs text-muted-foreground'>
                            {recommendation.reason}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  )
                })}
                <Button
                  type='button'
                  variant='secondary'
                  size='sm'
                  disabled={selectedRecommendationKeys.length === 0}
                  onClick={addSelectedRecommendations}
                >
                  {t(`${editDialogKey}.addSelectedRecommendations`)}
                </Button>
              </div>
            )}
          </div>
        </div>

        <SheetFooter className='shrink-0 flex-row justify-end gap-2 border-t px-4 py-4'>
          <Button
            type='button'
            variant='outline'
            onClick={() => {
              onOpenChange(false)
            }}
          >
            {t(`${editDialogKey}.cancel`)}
          </Button>
          <Button
            type='button'
            disabled={isSaving}
            onClick={() => {
              void handleSave()
            }}
          >
            {isSaving ? <Loader2 className='mr-2 size-4 animate-spin' /> : null}
            {t(`${editDialogKey}.save`)}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
